import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import SignaturePad from '../ui/SignaturePad';
import { captureSignTime, formatSignedLocal, formatSignedUtc } from '../../lib/signTime';
import useAutosave, { AUTOSAVE_LABEL } from '../../hooks/useAutosave';
import { useDefaultSignature } from '../../hooks/useDefaultSignature';
import {
  ArrowLeft, Save, Download, Printer, ChevronDown, Plus, Trash2, Copy,
  CheckCircle, XCircle, MinusCircle, FileSignature, Calculator, AlertTriangle, RotateCcw, Lock, Camera,
} from 'lucide-react';
import AuditTrailPanel from './AuditTrailPanel';
import FloatingActions from '../FloatingActions';
import { saveDoc, signDoc, isLocalId, cacheDocs } from '../../lib/offlineDocs';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { OfflineNotice, PendingSyncBadge } from '../OfflineNotice';
import EntityPhotoGallery from '../EntityPhotoGallery';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import ScrollX from '../shared/ScrollX';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_BASE}/api`;

const STATUS_META = {
  draft: { label: i18n.t('auto.lbl.draft'), color: 'bg-gray-100 text-gray-700' },
  in_progress: { label: i18n.t('auto.lbl.inProgress'), color: 'bg-blue-100 text-blue-700' },
  completed: { label: i18n.t('auto.lbl.approved'), color: 'bg-green-100 text-green-700' },
  conditional_approval: { label: i18n.t('auto.lbl.conditional'), color: 'bg-orange-100 text-orange-700' },
  signed_off: { label: i18n.t('auto.lbl.signedOff'), color: 'bg-emerald-100 text-emerald-700' },
};

const PF_BUTTONS = [
  { value: 'pass', label: i18n.t('auto.lbl.pass'), icon: CheckCircle, color: 'bg-green-100 text-green-700 border-green-300 ring-green-300' },
  { value: 'fail', label: i18n.t('auto.lbl.fail'), icon: XCircle, color: 'bg-red-100 text-red-700 border-red-300 ring-red-300' },
  { value: 'na', label: 'N/A', icon: MinusCircle, color: 'bg-gray-100 text-gray-600 border-gray-300 ring-gray-300' },
];

// EN 60898-1 protective-device trip-current multipliers used to compute the
// worst-case fault current Ia for an MCB. Zs_max = U0 / Ia.
//   Type B:  Ia = 5 × In   (instantaneous trip 3-5 × In)
//   Type C:  Ia = 10 × In  (5-10 × In)
//   Type D:  Ia = 20 × In  (10-20 × In)
const BREAKER_TYPES = [
  { key: 'B', label: i18n.t('auto.lbl.mcbTypeB'), mult: 5 },
  { key: 'C', label: i18n.t('auto.lbl.mcbTypeC'), mult: 10 },
  { key: 'D', label: i18n.t('auto.lbl.mcbTypeD'), mult: 20 },
];
const COMMON_RATINGS_A = [6, 10, 13, 16, 20, 25, 32, 40, 50, 63];

const BREAKER_PRESETS = BREAKER_TYPES.flatMap(t =>
  COMMON_RATINGS_A.map(r => ({
    value: `${t.label} ${r}A`,
    label: `${t.label} ${r}A`,
    type: t.key,
    rating: r,
    mult: t.mult,
  }))
);

/** Compute worst-case Zs allowed for a given preset string at U0. */
function maxZsForPreset(presetLabel, u0 = 230) {
  const p = BREAKER_PRESETS.find(b => b.value === presetLabel);
  if (!p) return null;
  const ia = p.mult * p.rating;
  if (!ia) return null;
  return (u0 / ia).toFixed(2);
}

/**
 * Resolve the Max-Allowed Zs for a row, with priority:
 *   1. User manual override   (row.max_zs_ohm_override === true)
 *   2. EN 60898-1 preset      (matches BREAKER_PRESETS)
 *   3. Custom breaker Ics     (row.ics_a, e.g. Siemens industrial breakers)
 *   4. null
 *
 * Always derived as a function of (row, u0) so updating the nominal
 * voltage in the header propagates through every row, including PASS/FAIL.
 */
function resolveMaxZs(row, u0 = 230) {
  if (row.max_zs_ohm_override) {
    const v = parseFloat(row.max_zs_ohm);
    return Number.isFinite(v) ? row.max_zs_ohm : null;
  }
  const fromPreset = maxZsForPreset(row.prot_device, u0);
  if (fromPreset != null) return fromPreset;
  const ics = parseFloat(row.ics_a);
  if (Number.isFinite(ics) && ics > 0) {
    return (u0 / ics).toFixed(2);
  }
  return null;
}

/** Compute row-level calculated fields. */
function computeDerived(row, test_type, nominalVoltage = 230) {
  const out = { ...row };
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  if (test_type === 'continuity') {
    // Expected R = rho * L / A (copper, rho = 17.2 nOhm.m -> 0.0172 Ohm.mm²/m)
    const L = num(row.length_m);
    const A = num(row.cross_section_mm2);
    if (L != null && A != null && A > 0 && !row.expected_resistance_override) {
      out.expected_resistance_ohm = (0.0172 * L / A).toFixed(4);
    }
    // Pass/fail hint — measured should be <= expected × 1.5 (20% tolerance allowance)
    const measured = num(out.measured_resistance_ohm);
    const expected = num(out.expected_resistance_ohm);
    if (measured != null && expected != null && !row.pass_fail_override) {
      out.pass_fail = measured <= expected * 1.5 ? 'pass' : 'fail';
    }
  }

  if (test_type === 'insulation') {
    const minOk = num(row.min_allowed_mohm);
    const vals = [
      'ir_l1_pe_mohm', 'ir_l2_pe_mohm', 'ir_l3_pe_mohm', 'ir_n_pe_mohm',
      'ir_l1_l2_mohm', 'ir_l1_l3_mohm', 'ir_l2_l3_mohm',
    ].map(k => num(row[k])).filter(v => v != null);
    if (minOk != null && vals.length && !row.pass_fail_override) {
      out.pass_fail = vals.every(v => v >= minOk) ? 'pass' : 'fail';
    }
  }

  if (test_type === 'fault_loop') {
    // Resolve Max-Allowed Zs from preset / Ics / override (function of U0
    // so changes to the nominal voltage propagate to PASS/FAIL).
    const resolvedMaxZs = resolveMaxZs(row, nominalVoltage);
    if (resolvedMaxZs != null) out.max_zs_ohm = resolvedMaxZs;
    else if (!row.max_zs_ohm_override) out.max_zs_ohm = '';

    // Ia = U0 / Zs per phase
    ['l1', 'l2', 'l3'].forEach(ph => {
      const zs = num(row[`zs_${ph}_ohm`]);
      const overrideKey = `ia_${ph}_a_override`;
      if (zs != null && zs > 0 && !row[overrideKey]) {
        out[`ia_${ph}_a`] = (nominalVoltage / zs).toFixed(1);
      }
    });
    const maxZs = num(out.max_zs_ohm);
    const zsVals = ['zs_l1_ohm', 'zs_l2_ohm', 'zs_l3_ohm'].map(k => num(row[k])).filter(v => v != null);
    if (maxZs != null && zsVals.length && !row.pass_fail_override) {
      const allWithin = zsVals.every(v => v <= maxZs);
      const discOk = String(row.disc_time_ok || '').toLowerCase() === 'yes';
      out.pass_fail = allWithin && (row.disc_time_ok == null || row.disc_time_ok === '' || discOk)
        ? 'pass' : 'fail';
    }
  }

  if (test_type === 'torque') {
    // 90% check value = required × 0.9 (NFPA 70B / IEC 60364-6 verification mode)
    const req = num(row.required_torque);
    if (req != null && req > 0 && !row.check_value_override) {
      out.check_value = (req * 0.9).toFixed(2);
    }
    if (!row.pass_fail_override) {
      const mode = String(row._test_mode || '').toLowerCase();
      const moved = String(row.moved_during_check || '').toLowerCase();
      const applied = num(row.applied_torque);

      if (mode === 'verification') {
        // FAIL if connection moved before reaching 90% check torque.
        if (moved === 'yes') out.pass_fail = 'fail';
        else if (moved === 'no' && applied != null && req != null && req > 0) {
          // pass if applied is within ±15% of the 90% check (operator stops there in this mode)
          const target = req * 0.9;
          out.pass_fail = applied >= target * 0.85 ? 'pass' : 'fail';
        }
      } else if (mode === 'new_installation') {
        // pass if applied within ±10% of required
        if (applied != null && req != null && req > 0) {
          out.pass_fail = (applied >= req * 0.9 && applied <= req * 1.1) ? 'pass' : 'fail';
        }
      }
    }
  }

  if (test_type === 'rcd') {
    // BS 7671 / IEC 61008-1 max-allowed operating time at IΔn — see seed
    // template description. supplyHeader is "TN-S/TN-C-S/TT/IT"; we only
    // disambiguate TT vs (TN/IT default to TN limits).
    const supplyHeader = String(row._supply_system || '').toUpperCase();
    const isTT = supplyHeader.includes('TT');
    const isS = String(row.s_type || '').toLowerCase() === 'yes';
    const purpose = row.purpose || 'additional_protection';

    let maxMs = null;
    if (isS) {
      maxMs = 500; // S-type cap (any purpose)
    } else if (purpose === 'additional_protection') {
      maxMs = 300; // BS 7671 Reg. 643.8 — both systems
    } else if (purpose === 'fault_protection_final') {
      maxMs = isTT ? 200 : 400;
    } else if (purpose === 'fault_protection_distribution') {
      maxMs = isTT ? 1000 : 5000;
    }

    if (maxMs != null && !row.max_allowed_ms_override) {
      out.max_allowed_ms = String(maxMs);
    }

    // Pass / fail aggregate. Auto-fill only when ALL the operator-entered
    // fields needed to make the call are present — never fail on missing data.
    if (!row.pass_fail_override) {
      const tb = String(row.test_button_ok || '').toLowerCase();
      const h0 = String(row.half_idn_0_no_trip || '').toLowerCase();
      const h180 = String(row.half_idn_180_no_trip || '').toLowerCase();
      const t1_0 = num(row.one_idn_0_ms);
      const t1_180 = num(row.one_idn_180_ms);
      const t5_0 = num(row.five_idn_0_ms);
      const t5_180 = num(row.five_idn_180_ms);
      const ramp = num(row.ramp_trip_ma);
      const idn = num(row.i_delta_n_ma);

      const allEntered =
        tb && h0 && h180 &&
        (t1_0 != null || t1_180 != null);

      if (allEntered) {
        const checks = [];
        checks.push(tb === 'yes');
        checks.push(h0 === 'yes');
        checks.push(h180 === 'yes');
        // 1×IΔn — worst case ≤ max allowed
        const worstAtIn = Math.max(t1_0 ?? 0, t1_180 ?? 0);
        if (maxMs != null) checks.push(worstAtIn > 0 ? worstAtIn <= maxMs : true);
        // S-type also has a min 130 ms (mustn't be too fast)
        if (isS && worstAtIn > 0) checks.push(worstAtIn >= 130);
        // 5×IΔn — only enforce when entered
        if (t5_0 != null || t5_180 != null) {
          const worst5 = Math.max(t5_0 ?? 0, t5_180 ?? 0);
          checks.push(worst5 <= (isS ? 150 : 40));
        }
        // Ramp — only enforce when entered + IΔn known
        if (ramp != null && idn != null && idn > 0) {
          checks.push(ramp >= idn * 0.5 && ramp <= idn);
        }
        out.pass_fail = checks.every(Boolean) ? 'pass' : 'fail';
      }
    }
  }
  return out;
}

const TestReportFillForm = ({ instance: initialInstance, template, token, currentUser, projects = [], onBack, onExportPDF }) => {
  const { t } = useTranslation();
  const { savedSignature, saveAsDefault } = useDefaultSignature();
  const [instance, setInstance] = useState(initialInstance);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloadHistory, setReloadHistory] = useState(0);
  const [photoRowIdx, setPhotoRowIdx] = useState(null);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const columns = template?.columns || [];
  const headerFields = template?.header_fields || [];
  const testType = instance.test_type;
  const nominalVoltage = parseFloat(instance.header_values?.nominal_voltage_v) || 230;
  const supplySystem = instance.header_values?.supply_system || '';
  const testMode = instance.header_values?.test_mode || '';

  // Auto-recompute derived fields whenever rows change
  const displayRows = useMemo(
    () => (instance.rows || []).map(r => computeDerived({ ...r, _supply_system: supplySystem, _test_mode: testMode }, testType, nominalVoltage)),
    [instance.rows, testType, nominalVoltage, supplySystem, testMode]
  );

  const updateCell = (rowIdx, key, value) => {
    setInstance(prev => {
      const rows = [...(prev.rows || [])];
      const row = { ...rows[rowIdx] };
      row[key] = value;
      // Track user overrides so auto-calc doesn't clobber
      if (key === 'expected_resistance_ohm') row.expected_resistance_override = true;
      if (key === 'pass_fail') row.pass_fail_override = true;
      if (key.startsWith('ia_') && key.endsWith('_a')) row[`${key}_override`] = true;
      if (key === 'max_zs_ohm') row.max_zs_ohm_override = true;
      // Picking a preset / changing breaker / typing Ics — clear any prior
      // manual override so the auto-calc takes over again.
      if (key === 'prot_device' || key === 'ics_a') {
        row.max_zs_ohm_override = false;
      }
      rows[rowIdx] = row;
      return { ...prev, rows };
    });
    setDirty(true);
  };

  /** Reset overrides so the row goes back to fully auto-calculated. */
  const resetOverrides = (rowIdx) => {
    setInstance(prev => {
      const rows = [...(prev.rows || [])];
      const row = { ...rows[rowIdx] };
      row.expected_resistance_override = false;
      row.pass_fail_override = false;
      row.max_zs_ohm_override = false;
      ['l1', 'l2', 'l3'].forEach(ph => { row[`ia_${ph}_a_override`] = false; });
      rows[rowIdx] = row;
      return { ...prev, rows };
    });
    setDirty(true);
  };

  const updateHeader = (key, value) => {
    setInstance(prev => ({ ...prev, header_values: { ...(prev.header_values || {}), [key]: value } }));
    setDirty(true);
  };

  const addRow = () => {
    setInstance(prev => {
      const rows = [...(prev.rows || [])];
      const newRow = { row_id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`, seq: rows.length + 1 };
      columns.forEach(col => { if (col.default != null && col.key !== 'seq') newRow[col.key] = col.default; });
      rows.push(newRow);
      return { ...prev, rows };
    });
    setDirty(true);
  };

  const duplicateRow = (idx) => {
    setInstance(prev => {
      const rows = [...(prev.rows || [])];
      const src = { ...rows[idx] };
      delete src.row_id;
      delete src.pass_fail_override;
      delete src.expected_resistance_override;
      const dup = { ...src, row_id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`, seq: rows.length + 1 };
      rows.push(dup);
      return { ...prev, rows };
    });
    setDirty(true);
  };

  const removeRow = (idx) => {
    setInstance(prev => {
      const rows = (prev.rows || []).filter((_, i) => i !== idx).map((r, i) => ({ ...r, seq: i + 1 }));
      return { ...prev, rows };
    });
    setDirty(true);
  };

  const save = useCallback(async (newStatus, { silent = false } = {}) => {
    setSaving(true);
    try {
      const payload = {
        header_values: instance.header_values,
        rows: displayRows, // persist with computed values baked in
        notes: instance.notes,
        appendix: instance.appendix,
      };
      if (newStatus) payload.status = newStatus;
      const { online, data } = await saveDoc({
        kind: 'test_report',
        id: instance.id,
        body: payload,
        doc: instance,
        template,
        label: `Test report ${instance.report_number || ''}`.trim(),
        token,
        onlineSave: async () => (
          await axios.put(`${API}/test-reports/instances/${instance.id}`, payload, authHeader)
        ).data,
      });
      setInstance(prev => ({ ...prev, ...(data || {}) }));
      setDirty(false);
      if (!silent) {
        if (online) setReloadHistory(k => k + 1);
        toast.success(online
          ? (newStatus ? `Saved — status: ${STATUS_META[data?.status]?.label || data?.status}` : 'Saved')
          : 'Saved on this device — it will sync when you\'re back online');
      }
    } catch (e) {
      if (silent) throw e;
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }, [instance, displayRows, authHeader]);

  const updateSignName = (idx, name) => {
    setInstance(prev => {
      const sign_offs = [...(prev.sign_offs || [])];
      sign_offs[idx] = { ...sign_offs[idx], name };
      return { ...prev, sign_offs };
    });
  };

  const updateSignImage = (idx, dataUrl) => {
    setInstance(prev => {
      const sign_offs = [...(prev.sign_offs || [])];
      sign_offs[idx] = { ...sign_offs[idx], signature_image: dataUrl };
      return { ...prev, sign_offs };
    });
  };

  // Default signatory name. For internal roles (Tested By / Approved By) we
  // pre-fill the logged-in user's name so they can confirm with one click,
  // but the field stays editable. Client Representative is always typed
  // from scratch.
  const defaultSignName = useCallback((role) => {
    const r = String(role || '').toLowerCase();
    if (r.includes('client') || r.includes('customer')) return '';
    return currentUser?.full_name || currentUser?.username || '';
  }, [currentUser]);

  const signRole = async (idx) => {
    const so = (instance.sign_offs || [])[idx];
    if (!so) return;
    const effectiveName = (so.name && so.name.trim()) || defaultSignName(so.role).trim();
    if (!effectiveName) {
      toast.error(t('auto.iso.enterTheSignatoryNameFirst'));
      return;
    }
    if (dirty) { toast.info(t('auto.iso.saveFirstBeforeSigning')); return; }
    const stamp = captureSignTime();
    const body = {
      role: so.role, name: effectiveName,
      signature_image: so.signature_image || null, ...stamp,
    };
    // Locally-signed shape used when the sign-off has to wait for signal.
    const signedLocally = {
      ...instance,
      sign_offs: (instance.sign_offs || []).map((s, i) => (
        i === idx
          ? { ...s, name: effectiveName, signed: true, signed_at: new Date().toISOString(), ...stamp, pending_sync: true }
          : s
      )),
    };
    try {
      const { online } = await signDoc({
        kind: 'test_report',
        id: instance.id,
        roleKey: so.role,
        body,
        doc: signedLocally,
        template,
        label: `Sign-off (${so.role}) — ${instance.report_number || 'test report'}`,
        token,
        onlineSign: async () => (
          await axios.post(`${API}/test-reports/instances/${instance.id}/sign`, body, authHeader)
        ).data,
      });
      if (online) {
        toast.success(`Signed as ${so.role}`);
        const resp = await axios.get(`${API}/test-reports/instances/${instance.id}`, authHeader);
        setInstance(resp.data);
        setReloadHistory(k => k + 1);
      } else {
        setInstance(signedLocally);
        toast.success('Signed on this device — it will upload when you\'re back online');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Sign failed'); }
  };

  const revertDraft = async () => {
    if (!window.confirm(t('auto.iso.revertToDraftSignaturesWillBe'))) return;
    try {
      const cleared = (instance.sign_offs || []).map(s => ({
        role: s.role, signed: false, signed_at: null, user_id: null, name: null,
      }));
      // Persist BOTH status and cleared sign_offs in one call (sign_offs is allowed in PUT)
      await axios.put(`${API}/test-reports/instances/${instance.id}`, {
        status: 'draft',
        sign_offs: cleared,
      }, authHeader);
      toast.success(t('auto.iso.revertedToDraftSignaturesCleared'));
      const resp = await axios.get(`${API}/test-reports/instances/${instance.id}`, authHeader);
      setInstance(resp.data);
      setDirty(false);
      setReloadHistory(k => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Stats
  const total = displayRows.length;
  const pass = displayRows.filter(r => r.pass_fail === 'pass').length;
  const fail = displayRows.filter(r => r.pass_fail === 'fail').length;
  const na = displayRows.filter(r => r.pass_fail === 'na').length;
  const filled = pass + fail + na;

  const sMeta = STATUS_META[instance.status] || STATUS_META.draft;
  const isAdmin = currentUser?.role === 'admin';
  // Once a report is signed off it's locked against further edits —
  // admins AND the report's creator may re-open / amend it.
  const isCreator = !!currentUser && currentUser.id === instance?.created_by;
  const isLocked = instance.status === 'signed_off' && !(isAdmin || isCreator);
  const canRevert = instance.status !== 'draft' && (isAdmin || isCreator);

  const isOnline = useOnlineStatus();
  const pendingSync = isLocalId(instance?.id);

  // Keep every opened report on the device so it can be filled in
  // later with no signal.
  useEffect(() => {
    if (!isOnline || !instance?.id) return;
    cacheDocs('test_report', [instance], template).catch(() => { /* storage unavailable */ });
    // eslint-disable-next-line
  }, [instance?.id, template?.id, isOnline]);

  // Autosave — drafts / in-progress only, 1.5 s after typing stops.
  const autosaveState = useAutosave(
    { h: instance.header_values, r: displayRows, n: instance.notes, a: instance.appendix },
    () => save(undefined, { silent: true }),
    { enabled: !isLocked && ['draft', 'in_progress'].includes(instance.status) },
  );

  return (
    <div className="space-y-3" data-testid="test-report-fill-form">
      <OfflineNotice
        isOnline={isOnline && !pendingSync}
        cached={!isOnline || pendingSync}
        what="report"
        testId="test-report-offline-banner"
      />
      {isLocked && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm"
          data-testid="lock-notice"
        >
          <Lock className="h-4 w-4 shrink-0" />
          <div>
            <strong>{t('auto.iso.thisReportIsSignedOffAnd')}</strong>{' '}
            Only an administrator can re-open or amend it. You can still
            view, sign (if your role is pending) and export the PDF.
          </div>
        </div>
      )}
      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-2 sticky top-0 bg-white z-10 pb-2 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold">{instance.report_number}</span>
              {pendingSync && <PendingSyncBadge testId="test-report-pending-badge" />}
              <Badge className={`${sMeta.color} text-xs font-normal`}>{sMeta.label}</Badge>
              {instance.status === 'signed_off' && (
                <Badge
                  className="text-xs font-normal bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1"
                  data-testid="lock-badge"
                  title={isAdmin ? t('auto.iso.signedOffAdminOverrideAvailable') : t('auto.iso.lockedOnlyAdminsCanReOpen2')}
                >
                  <Lock className="h-3 w-3" /> {isAdmin ? t('auto.iso.lockedAdmin') : t('auto.iso.locked')}
                </Badge>
              )}
              <span className="text-xs text-gray-500">{instance.doc_ref} {instance.revision}</span>
            </div>
            <div className="text-sm text-gray-700 truncate">{instance.template_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-gray-500 mr-1">
            <span className="text-green-600 font-medium">{pass}</span>/<span className="text-red-600 font-medium">{fail}</span>/<span className="text-gray-500">{na}</span> of {total}
          </span>
          <span className="text-xs text-slate-500 mr-1" data-testid="report-autosave-indicator">
            {AUTOSAVE_LABEL[autosaveState]}
          </span>
          <Button variant="outline" size="sm" disabled={!dirty || saving || isLocked} onClick={() => save()} data-testid="save-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? t('auto.iso.saving') : t('auto.iso.save')}
          </Button>
          {instance.status === 'draft' && (
            <Button variant="outline" size="sm" onClick={() => save('in_progress')} disabled={saving || isLocked} data-testid="btn-mark-in-progress">
              {t('auto.iso.markInProgress')}
            </Button>
          )}
          {['draft', 'in_progress', 'conditional_approval'].includes(instance.status) && (
            <Button size="sm" onClick={() => save('completed')} disabled={saving || isLocked} data-testid="approve-btn">
              <CheckCircle className="h-4 w-4 mr-1" /> {t('auto.iso.approve')}
            </Button>
          )}
          {canRevert && (
            <Button variant="outline" size="sm" onClick={revertDraft} data-testid="btn-revertdraft">
              <RotateCcw className="h-4 w-4 mr-1" /> {t('auto.iso.revertToDraft')}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> {t('auto.iso.pdf')} <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onExportPDF && onExportPDF(false, false)}>
                <Download className="h-4 w-4 mr-2" /> {t('auto.iso.colorPdf')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportPDF && onExportPDF(true, false)}>
                <Printer className="h-4 w-4 mr-2" /> Print-friendly (B&amp;W)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportPDF && onExportPDF(false, true)} data-testid="export-pdf-with-photos">
                <Camera className="h-4 w-4 mr-2" /> {t('auto.iso.colorPhotoEvidence')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportPDF && onExportPDF(true, true)} data-testid="export-pdf-bw-with-photos">
                <Camera className="h-4 w-4 mr-2" /> B&amp;W + Photo Evidence
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Description */}
      {template?.description && (
        <div className="text-xs text-gray-600 bg-gray-50 border-l-2 border-gray-200 p-2 rounded-r">
          <span className="font-semibold text-gray-700">{template.standard_reference}</span> — {template.description}
        </div>
      )}

      {/* Editable body — natively disabled when the report is locked
          (keeps the action toolbar and sign-offs section interactive). */}
      <fieldset disabled={isLocked} className={`m-0 min-w-0 p-0 border-0 space-y-3 ${isLocked ? 'opacity-60' : ''}`}>
      {/* Header fields */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t('auto.iso.reportDetails')}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {headerFields.map(f => (
              <div key={f.key}>
                <label className="text-xs font-medium text-gray-600">{f.label}</label>
                {f.type === 'select' && Array.isArray(f.options) ? (
                  <Select
                    value={instance.header_values?.[f.key] || '__unset__'}
                    onValueChange={(v) => updateHeader(f.key, v === '__unset__' ? '' : v)}
                  >
                    <SelectTrigger className="h-8 text-sm mt-1" data-testid={`header-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unset__">—</SelectItem>
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    className="h-8 text-sm mt-1"
                    type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                    value={instance.header_values?.[f.key] ?? ''}
                    onChange={(e) => updateHeader(f.key, e.target.value)}
                    data-testid={`header-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          {testType === 'torque' && (
            <div className="mt-3 pt-3 border-t flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer" title={t('auto.iso.appendAReferenceTorqueTableTo')}>
                <input
                  type="checkbox"
                  checked={!!instance.appendix?.include_torque_reference_table}
                  onChange={(e) => setInstance((p) => ({ ...p, appendix: { ...(p.appendix || {}), include_torque_reference_table: e.target.checked } }))}
                  data-testid="appendix-ref-table"
                />
                {t('auto.iso.appendReferenceTorqueTableToPdf')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" title={t('auto.iso.appendTheNormativeReferencesToThe')}>
                <input
                  type="checkbox"
                  checked={!!instance.appendix?.include_normative_references}
                  onChange={(e) => setInstance((p) => ({ ...p, appendix: { ...(p.appendix || {}), include_normative_references: e.target.checked } }))}
                  data-testid="appendix-references"
                />
                {t('auto.iso.appendNormativeReferencesToPdf')}
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rows table */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            {t('auto.iso.testRecords')}
            <Badge variant="outline" className="text-xs">
              <Calculator className="h-3 w-3 mr-1" /> {t('auto.iso.autoCalcOn')}
            </Badge>
          </CardTitle>
          <Button size="sm" variant="outline" onClick={addRow} data-testid="add-row-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('auto.iso.addRow')}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollX testId="test-records-scroll" barClassName="px-2">
            <table className="w-full min-w-max text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] border-y">
                <tr>
                  {columns.map(c => (
                    <th key={c.key} className="text-left px-2 py-1.5 whitespace-nowrap" style={{ minWidth: `${c.width || 80}px` }}>
                      {c.label}
                      {c.unit && <span className="ml-1 normal-case text-[9px] text-gray-400">({c.unit})</span>}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-right w-[80px]">{t('auto.iso.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => (
                  <tr key={row.row_id || idx} className="border-t hover:bg-gray-50/50">
                    {columns.map(col => {
                      const key = col.key;
                      const val = row[key] ?? '';
                      const isOverride = row.expected_resistance_override && key === 'expected_resistance_ohm';
                      if (col.type === 'sequence') {
                        return <td key={key} className="px-2 py-1.5 text-gray-500 font-mono">{row.seq}</td>;
                      }
                      if (col.type === 'pass_fail') {
                        return (
                          <td key={key} className="px-2 py-1.5">
                            <div className="flex gap-0.5">
                              {PF_BUTTONS.map(b => {
                                const BtnIcon = b.icon;
                                const active = val === b.value;
                                return (
                                  <button
                                    key={b.value}
                                    className={`w-6 h-6 flex items-center justify-center rounded border text-[10px] ${active ? b.color + ' font-bold ring-1' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}
                                    onClick={() => updateCell(idx, key, b.value)}
                                    title={b.label}
                                    data-testid={`row-${idx}-${b.value}`}
                                  >
                                    <BtnIcon className="h-3 w-3" />
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        );
                      }
                      if (col.type === 'yes_no') {
                        return (
                          <td key={key} className="px-2 py-1.5">
                            <Select value={val || 'unset'} onValueChange={(v) => updateCell(idx, key, v === 'unset' ? '' : v)}>
                              <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unset">—</SelectItem>
                                <SelectItem value="Yes">{t('auto.iso.yes')}</SelectItem>
                                <SelectItem value="No">{t('auto.iso.no')}</SelectItem>
                                <SelectItem value="N/A">N/A</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        );
                      }
                      // Protective device (fault_loop only): combined preset
                      // dropdown + free-text fallback. Picking a preset
                      // auto-computes the max-allowed Zs. For non-preset
                      // (industrial) breakers, an Ics input lets the user
                      // enter the breaking current directly so we can compute
                      // Max Zs = U₀ / Ics.
                      if (testType === 'fault_loop' && key === 'prot_device') {
                        const presetMatch = BREAKER_PRESETS.find(p => p.value === val);
                        const isCustom = !presetMatch;
                        return (
                          <td key={key} className="px-1 py-1">
                            <div className="flex flex-col gap-0.5">
                              <Select
                                value={presetMatch ? presetMatch.value : 'custom'}
                                onValueChange={(v) => {
                                  if (v === 'custom') {
                                    // Picking "custom" — clear the prot_device so it falls
                                    // through to Ics-based calc when an Ics is set.
                                    updateCell(idx, 'prot_device', '');
                                    return;
                                  }
                                  updateCell(idx, 'prot_device', v);
                                  // Switching to a preset clears the Ics override
                                  if (row.ics_a) updateCell(idx, 'ics_a', '');
                                }}
                              >
                                <SelectTrigger className="h-6 text-[10px] px-1.5" data-testid={`row-${idx}-prot-device-preset`}>
                                  <SelectValue placeholder={t('auto.iso.pickPreset')} />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                  <SelectItem value="custom">{t('auto.iso.customIndustrialBreaker')}</SelectItem>
                                  {BREAKER_TYPES.map(t => (
                                    <React.Fragment key={t.key}>
                                      {COMMON_RATINGS_A.map(r => (
                                        <SelectItem key={`${t.key}-${r}`} value={`${t.label} ${r}A`}>
                                          {t.label} {r}A
                                        </SelectItem>
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                className="h-6 text-[10px]"
                                type="text"
                                value={val}
                                onChange={(e) => updateCell(idx, key, e.target.value)}
                                placeholder="e.g. Siemens 3VA"
                                data-testid={`row-${idx}-${key}`}
                              />
                              {isCustom && (
                                <Input
                                  className="h-6 text-[10px]"
                                  type="number"
                                  step="any"
                                  value={row.ics_a || ''}
                                  onChange={(e) => updateCell(idx, 'ics_a', e.target.value)}
                                  placeholder="Ics (A) — e.g. 52"
                                  title={t('auto.iso.operatingShortCircuitBreakingCapacityA')}
                                  data-testid={`row-${idx}-ics-a`}
                                />
                              )}
                            </div>
                          </td>
                        );
                      }
                      // RCD-specific Selects: type / IΔn / purpose
                      if (testType === 'rcd' && key === 'rcd_type') {
                        return (
                          <td key={key} className="px-1 py-1">
                            <Select value={val || 'unset'} onValueChange={(v) => updateCell(idx, key, v === 'unset' ? '' : v)}>
                              <SelectTrigger className="h-7 text-xs" data-testid={`row-${idx}-${key}`}><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unset">—</SelectItem>
                                <SelectItem value="AC">{t('auto.iso.ac')}</SelectItem>
                                <SelectItem value="A">A</SelectItem>
                                <SelectItem value="F">F</SelectItem>
                                <SelectItem value="B">B</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        );
                      }
                      if (testType === 'rcd' && key === 'i_delta_n_ma') {
                        const presets = [10, 30, 100, 300, 500];
                        const isPreset = presets.includes(parseInt(val, 10));
                        return (
                          <td key={key} className="px-1 py-1">
                            <div className="flex flex-col gap-0.5">
                              <Select
                                value={isPreset ? String(parseInt(val, 10)) : 'custom'}
                                onValueChange={(v) => {
                                  if (v === 'custom') return;
                                  updateCell(idx, key, v);
                                }}
                              >
                                <SelectTrigger className="h-6 text-[10px] px-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                  {presets.map((p) => <SelectItem key={p} value={String(p)}>{p} mA</SelectItem>)}
                                  <SelectItem value="custom">{t('auto.iso.custom')}</SelectItem>
                                </SelectContent>
                              </Select>
                              {!isPreset && (
                                <Input
                                  className="h-6 text-[10px]"
                                  type="number"
                                  step="any"
                                  value={val}
                                  onChange={(e) => updateCell(idx, key, e.target.value)}
                                  placeholder={t('auto.iso.ma')}
                                  data-testid={`row-${idx}-${key}`}
                                />
                              )}
                            </div>
                          </td>
                        );
                      }
                      if (testType === 'rcd' && key === 'purpose') {
                        return (
                          <td key={key} className="px-1 py-1">
                            <Select value={val || 'additional_protection'} onValueChange={(v) => updateCell(idx, key, v)}>
                              <SelectTrigger className="h-7 text-[10px]" data-testid={`row-${idx}-${key}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="additional_protection">{t('auto.iso.additionalProtection30Ma')}</SelectItem>
                                <SelectItem value="fault_protection_final">{t('auto.iso.faultProtFinalCircuit')}</SelectItem>
                                <SelectItem value="fault_protection_distribution">{t('auto.iso.faultProtDistribution')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        );
                      }
                      // text or number input
                      return (
                        <td key={key} className="px-1 py-1">
                          <Input
                            className={`h-7 text-xs ${col.type === 'calculated' && !isOverride ? 'bg-blue-50/50 text-blue-900' : ''}`}
                            type={col.type === 'number' || col.type === 'calculated' ? 'number' : 'text'}
                            step="any"
                            value={val}
                            onChange={(e) => updateCell(idx, key, e.target.value)}
                            placeholder={col.type === 'calculated' ? 'auto' : ''}
                            title={col.type === 'calculated' ? 'Auto-calculated — edit to override' : ''}
                            data-testid={`row-${idx}-${key}`}
                          />
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPhotoRowIdx(idx)}
                        title={t('auto.iso.photosForThisMeasurement')}
                        className="h-6 w-6 p-0 relative"
                        data-testid={`row-${idx}-photos-btn`}
                      >
                        <Camera className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => duplicateRow(idx)} title={t('auto.iso.copyRow')} className="h-6 w-6 p-0">
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => resetOverrides(idx)} title={t('auto.iso.resetOverrides')} className="h-6 w-6 p-0">
                        <RotateCcw className="h-3 w-3 text-blue-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeRow(idx)} title={t('auto.iso.removeRow')} className="h-6 w-6 p-0">
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
          {displayRows.length === 0 && (
            <div className="py-6 text-center text-xs text-gray-400">{t('auto.iso.noRowsClickAddRowTo')}</div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t('auto.iso.notes')}</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            className="text-sm"
            rows={3}
            placeholder={t('auto.iso.anyObservationsAnomaliesOrComments')}
            value={instance.notes || ''}
            onChange={(e) => { setInstance(prev => ({ ...prev, notes: e.target.value  })); setDirty(true); }}
            data-testid="notes-textarea"
          />
        </CardContent>
      </Card>

      {/* Report-level photos (e.g. test-setup picture, equipment shot) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Camera className="h-4 w-4" /> {t('auto.iso.reportPhotos')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EntityPhotoGallery
            entityType="test_report"
            entityId={instance.id}
            itemKey={null}
            readOnly={isLocked}
          />
        </CardContent>
      </Card>

      {/* Per-row photo dialog */}
      <Dialog open={photoRowIdx !== null} onOpenChange={(o) => !o && setPhotoRowIdx(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Photos for measurement #{photoRowIdx !== null ? (displayRows[photoRowIdx]?.seq ?? photoRowIdx + 1) : ''}
            </DialogTitle>
          </DialogHeader>
          {photoRowIdx !== null && (
            <EntityPhotoGallery
              entityType="test_report"
              entityId={instance.id}
              itemKey={`measurement.${displayRows[photoRowIdx]?.row_id || photoRowIdx}`}
              readOnly={isLocked}
            />
          )}
        </DialogContent>
      </Dialog>

      </fieldset>

      {/* Sign-offs */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileSignature className="h-4 w-4" /> {t('auto.iso.authorisationSignatures')}</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(instance.sign_offs || []).map((so, i) => {
              const placeholder = defaultSignName(so.role);
              const inputVal = so.name ?? (placeholder || '');
              const canSign = !dirty && !!(inputVal && inputVal.trim());
              return (
                <div key={i} className="p-2 border rounded space-y-2">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0 sm:min-w-[200px] w-full sm:w-auto">
                      <div className="text-sm font-medium">{so.role}</div>
                      {so.signed ? (
                        <div className="text-xs text-green-600 space-y-1">
                          <div className="flex items-center gap-1.5">
                            <span>Signed by {so.name || '—'}</span>
                            {so.pending_sync && <PendingSyncBadge testId={`report-sign-pending-${i}`} label="Pending sync" />}
                          </div>
                          <div className="text-gray-500" data-testid={`report-signed-at-${i}`}>
                            {formatSignedLocal(so)}
                            <span className="block text-[10px] text-gray-400">{formatSignedUtc(so)}</span>
                          </div>
                          {so.signature_image && (
                            <img
                              src={so.signature_image}
                              alt={`${so.role} signature`}
                              className="max-h-16 border rounded bg-white p-1"
                              data-testid={`signed-sig-img-${i}`}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">
                          {placeholder
                            ? 'Pre-filled from your profile — edit if needed, then click Sign'
                            : 'Type the client representative name, then click Sign'}
                        </div>
                      )}
                    </div>
                    {!so.signed && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                        <Input
                          className="h-11 sm:h-8 text-base sm:text-sm w-full sm:w-48"
                          placeholder={placeholder || 'Enter signatory name'}
                          value={inputVal}
                          onChange={(e) => updateSignName(i, e.target.value)}
                          data-testid={`sign-name-${i}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full sm:w-auto h-11 sm:h-8 text-base sm:text-sm"
                          onClick={() => signRole(i)}
                          disabled={!canSign}
                          data-testid={`sign-btn-${i}`}
                        >
                          <FileSignature className="h-3.5 w-3.5 mr-1" /> {t('auto.iso.sign')}
                        </Button>
                      </div>
                    )}
                  </div>
                  {!so.signed && (
                    <div className="w-full sm:w-[320px]">
                      <SignaturePad
                        value={so.signature_image || null}
                        onChange={(dataUrl) => updateSignImage(i, dataUrl)}
                        height={100}
                        testIdPrefix={`tr-sign-pad-${i}`}
                        savedSignature={savedSignature}
                        onSaveDefault={async (dataUrl) => {
                          const ok = await saveAsDefault(dataUrl);
                          toast[ok ? 'success' : 'error'](ok ? t('auto.iso.savedAsYourDefaultSignature') : t('auto.iso.failedToSaveDefault'));
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {dirty && (
            <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" /> {t('auto.iso.saveBeforeSigning')}
            </div>
          )}
        </CardContent>
      </Card>

      <AuditTrailPanel kind="test-report" instanceId={instance.id} reloadKey={reloadHistory} />

      <FloatingActions actions={[
        { icon: Save, label: saving ? t('auto.iso.saving2') : t('auto.iso.save'), tone: 'primary',
          testId: 'report-floating-save-btn', disabled: !dirty || saving || isLocked,
          onClick: () => save() },
        { icon: CheckCircle, label: t('auto.lbl.approve'), tone: 'success',
          testId: 'report-floating-approve-btn',
          hidden: !['draft', 'in_progress', 'conditional_approval'].includes(instance.status),
          disabled: saving || isLocked, onClick: () => save('completed') },
        { icon: Download, label: t('auto.lbl.exportPdf'), testId: 'report-floating-pdf-btn',
          onClick: () => onExportPDF && onExportPDF(false, false) },
      ]} />
    </div>
  );
};

export default TestReportFillForm;
